import { useState } from "react";

interface Product {
  id: string;
  title: string;
  unitPrice: number;
  moqUnits: number;
  moqUSD: number;
  containerQty: Record<string, number>;
  image: string;
  customizable: boolean;
}

interface Factory {
  name: string;
  flag: string;
  minOrderUSD: number;
  containerTypes: string[];
  bulkLeadTime: string;
  samplePrice: number;
}

interface BulkOrderModalProps {
  factory: Factory;
  product: Product;
  onClose: () => void;
}

export default function BulkOrderModal({ factory, product, onClose }: BulkOrderModalProps) {
  const [orderType, setOrderType] = useState<"units" | "container">("container");
  const [containerType, setContainerType] = useState(factory.containerTypes[0]);
  const [containerQty, setContainerQty] = useState(1);
  const [unitQty, setUnitQty] = useState(product.moqUnits);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", country: "", port: "", notes: "", customBranding: false });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const unitsPerContainer = product.containerQty[containerType] || 0;
  const containerTotal = unitsPerContainer * containerQty * product.unitPrice;
  const unitTotal = unitQty * product.unitPrice;
  const displayTotal = orderType === "container" ? containerTotal : unitTotal;
  const displayUnits = orderType === "container" ? unitsPerContainer * containerQty : unitQty;

  const isValidOrder = displayTotal >= factory.minOrderUSD;

  const handleSubmit = () => {
    if (!form.name || !form.email || !form.country || !isValidOrder) return;
    setLoading(true);
    setTimeout(() => { setLoading(false); setSubmitted(true); }, 1400);
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-white rounded-2xl w-full max-w-md p-10 text-center shadow-xl">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-orange-100 text-orange-500 text-3xl mx-auto mb-4">
            <i className="ri-ship-line" />
          </div>
          <h2 className="text-xl font-extrabold text-gray-900 mb-2">Bulk Order Submitted!</h2>
          <p className="text-gray-500 text-sm mb-2">
            <strong>{factory.name}</strong> will review your order for <strong>{product.title}</strong>.
          </p>
          <div className="bg-orange-50 rounded-xl p-4 mb-4 text-left space-y-1.5">
            <div className="flex justify-between text-sm"><span className="text-gray-500">Order Value</span><span className="font-bold text-gray-900">${displayTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-500">Units</span><span className="font-bold text-gray-900">{displayUnits.toLocaleString()}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-500">Lead Time</span><span className="font-bold text-orange-600">{factory.bulkLeadTime}</span></div>
          </div>
          <p className="text-xs text-gray-400 mb-6">The factory will contact you at {form.email} within 48 hours with a formal proforma invoice and payment instructions.</p>
          <button onClick={onClose} className="px-8 py-3 bg-orange-500 text-white font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-extrabold text-gray-900 flex items-center gap-2">
            <i className="ri-ship-line text-orange-500" /> Bulk / Container Order
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer text-gray-400">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Product Preview */}
          <div className="flex gap-3 bg-orange-50 border border-orange-100 rounded-xl p-4">
            <img src={product.image} alt={product.title} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
            <div>
              <p className="text-sm font-bold text-gray-900">{product.title}</p>
              <p className="text-xs text-gray-500 mt-0.5">{factory.flag} {factory.name}</p>
              <p className="text-xs text-gray-600 mt-1">Unit price: <strong>${product.unitPrice.toFixed(2)}</strong> · MOQ: <strong>${factory.minOrderUSD.toLocaleString()}+</strong></p>
            </div>
          </div>

          {/* Order Type Toggle */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Order Type</label>
            <div className="flex gap-2">
              {(["container", "units"] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setOrderType(t)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-colors cursor-pointer ${orderType === t ? "bg-orange-500 text-white" : "border border-gray-200 text-gray-600 hover:border-orange-300"}`}
                >
                  {t === "container" ? <span><i className="ri-ship-line mr-1" />By Container</span> : <span><i className="ri-stack-line mr-1" />By Units</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Container Config */}
          {orderType === "container" && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Container Type</label>
                <div className="flex flex-wrap gap-2">
                  {factory.containerTypes.map((ct) => (
                    <button
                      key={ct}
                      onClick={() => setContainerType(ct)}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors cursor-pointer border ${containerType === ct ? "bg-orange-500 text-white border-orange-500" : "border-gray-200 text-gray-600 hover:border-orange-300"}`}
                    >
                      <i className="ri-truck-line mr-1" />{ct}
                      {product.containerQty[ct] && <span className="ml-1 text-xs opacity-80">({product.containerQty[ct].toLocaleString()} units)</span>}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Number of Containers</label>
                <div className="flex items-center gap-3">
                  <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                    <button onClick={() => setContainerQty(Math.max(1, containerQty - 1))} className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-50 cursor-pointer"><i className="ri-subtract-line" /></button>
                    <span className="w-12 text-center text-sm font-bold">{containerQty}</span>
                    <button onClick={() => setContainerQty(containerQty + 1)} className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-50 cursor-pointer"><i className="ri-add-line" /></button>
                  </div>
                  <span className="text-sm text-gray-500">{(unitsPerContainer * containerQty).toLocaleString()} units total</span>
                </div>
              </div>
            </div>
          )}

          {/* Units Config */}
          {orderType === "units" && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Quantity (min. {product.moqUnits.toLocaleString()} units)
              </label>
              <input
                type="number"
                value={unitQty}
                min={product.moqUnits}
                onChange={(e) => setUnitQty(Math.max(product.moqUnits, parseInt(e.target.value) || product.moqUnits))}
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-orange-400"
              />
            </div>
          )}

          {/* Order Summary */}
          <div className={`rounded-xl p-4 border ${isValidOrder ? "bg-orange-50 border-orange-200" : "bg-red-50 border-red-200"}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">Order Summary</span>
              {!isValidOrder && (
                <span className="text-xs text-red-600 font-semibold">
                  <i className="ri-error-warning-line mr-1" />Below MOQ (${factory.minOrderUSD.toLocaleString()})
                </span>
              )}
            </div>
            <div className="space-y-1.5">
              <div className="flex justify-between text-sm"><span className="text-gray-500">Units</span><span className="font-semibold">{displayUnits.toLocaleString()}</span></div>
              <div className="flex justify-between text-sm"><span className="text-gray-500">Unit Price</span><span className="font-semibold">${product.unitPrice.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm border-t border-orange-200 pt-1.5 mt-1.5">
                <span className="font-bold text-gray-900">Total Value</span>
                <span className={`text-lg font-extrabold ${isValidOrder ? "text-orange-600" : "text-red-500"}`}>
                  ${displayTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>Lead Time</span><span className="font-semibold text-orange-600">{factory.bulkLeadTime}</span>
              </div>
            </div>
          </div>

          {/* Custom Branding */}
          {product.customizable && (
            <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-50 rounded-xl border border-gray-100">
              <input type="checkbox" checked={form.customBranding} onChange={(e) => setForm({ ...form, customBranding: e.target.checked })} className="accent-orange-500 w-4 h-4" />
              <div>
                <p className="text-sm font-semibold text-gray-900">Add Custom Branding / Private Label</p>
                <p className="text-xs text-gray-500">Your logo, packaging design, and brand name on all products</p>
              </div>
            </label>
          )}

          {/* Contact */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Full Name *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Company *</label>
              <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company name" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Email *</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="your@email.com" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Country *</label>
              <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Destination country" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Destination Port / City</label>
              <input value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} placeholder="e.g. Port of Los Angeles, Hamburg, Singapore" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Additional Notes</label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} maxLength={500} placeholder="Specific requirements, delivery terms (FOB/CIF/EXW), branding details..." className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400 resize-none" />
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 text-sm text-orange-800">
            <i className="ri-information-line mr-2" />
            <strong>Payment:</strong> Factory will send a Proforma Invoice. Payment via Wire Transfer, L/C, or PayPal (for orders under $50K). Sample cost (${factory.samplePrice}) is deducted from first bulk order.
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading || !form.name || !form.email || !form.country || !isValidOrder}
            className="w-full py-3.5 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {loading
              ? <span><i className="ri-loader-4-line animate-spin mr-2" />Submitting...</span>
              : <span><i className="ri-ship-line mr-2" />Submit Bulk Order — ${displayTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            }
          </button>
        </div>
      </div>
    </div>
  );
}
